import { useMemo } from 'react';
import { Icon, type IconName } from '../chrome/Icon';
import { fmtRange } from '../shared/statusMeta';
import { StatusPill } from '../shared/StatusPill';
import { useCatalogOrBlob } from '../media/useMediaBlob';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useT } from '../../i18n';
import type { MediaRow } from '../../data/dto';
import type { Milestone } from '../../data/types';

interface Props {
  m: Milestone;
  /** Baby's ISO birthdate — turns the week range into real calendar dates. */
  dobIso?: string;
  /** Baby's current age in whole weeks (derived from birthdate). Drives warning/late. */
  babyAgeWeeks?: number;
  onToggle?: (showTiming?: boolean) => void;
  onCycle?: () => void;
  accent?: string;
  accentInk?: string;
  expanded?: boolean;
}

const CAT_ICONS: Record<string, { icon: IconName; color: string }> = {
  'Motor Skills':       { icon: 'play',    color: '#D98E5A' },
  'Communication':      { icon: 'sing',    color: '#A579B0' },
  'Social & Emotional': { icon: 'explore', color: '#5E92B0' },
  'Cognitive':          { icon: 'read',    color: '#6F9E6E' },
};

export function MilestoneCard({ m, dobIso, babyAgeWeeks, onToggle, onCycle }: Props) {
  const enc = useEncryption();
  const t = useT();

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    try {
      return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return iso;
    }
  };

  const getCalendarWeek = (date: Date): number => {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const expectedCw = useMemo(() => {
    if (!dobIso) return `expected wk ${Math.round(m.medianWeeks)}`;
    const dob = new Date(dobIso + 'T00:00:00Z');
    dob.setUTCDate(dob.getUTCDate() + Math.round(m.medianWeeks * 7));
    const cw = getCalendarWeek(dob);
    return `expected CW ${cw}`;
  }, [dobIso, m.medianWeeks]);

  const isWarning = m.status !== 'done' && babyAgeWeeks !== undefined && babyAgeWeeks > m.earlyFlagWeeks;
  const isInfo = m.status !== 'done' && babyAgeWeeks !== undefined && !isWarning && babyAgeWeeks > (m.medianWeeks + m.stdDevWeeks);
  const barColor =
    m.status === 'done' ? 'bg-accent' :
    m.status === 'now'  ? 'bg-amber'  :
                          'bg-grey';

  const meta  = CAT_ICONS[m.cat] ?? { icon: 'journey', color: 'var(--color-ink-3)' };

  // Public editorial illustrations load via a plain <img src> (native lazy +
  // browser HTTP cache); only parent photos (private / possibly E2EE) go
  // through the authed fetch→decrypt→objectURL path.
  const { url } = useCatalogOrBlob(
    m.primaryMedia
      ? ({
          id: m.primaryMedia.id,
          mime_type: m.primaryMedia.mimeType,
          is_e2e_encrypted: m.primaryMedia.isEncrypted,
        } as MediaRow)
      : ({} as MediaRow),
    !!m.primaryMedia?.isCatalog && m.primaryMedia.kind === 'image',
    enc.status === 'ready',
    enc.decrypt,
    !!m.primaryMedia,
    true,
  );

  const dateFromWeeks = (weeks: number) => {
    if (!dobIso) return '';
    try {
      const d = new Date(dobIso + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() + Math.round(weeks * 7));
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return '';
    }
  };

  const renderTimingStats = () => {
    const startW = Math.round(m.startWeeks);
    const medianW = Math.round(m.medianWeeks);
    const stdDevStr = m.stdDevWeeks % 1 === 0 ? String(m.stdDevWeeks) : m.stdDevWeeks.toFixed(1);

    if (m.status === 'done') {
      const dateStr = m.achievedDate ? formatDate(m.achievedDate) : '';
      let diffStr = '';
      if (dobIso && m.achievedDate) {
        const dob = new Date(dobIso + 'T00:00:00Z').getTime();
        const achieved = new Date(m.achievedDate + 'T00:00:00Z').getTime();
        const achievedWeeks = (achieved - dob) / (7 * 86_400_000);
        const diff = achievedWeeks - m.medianWeeks;
        const diffRound = Math.round(diff * 10) / 10;
        
        if (diffRound < 0) {
          diffStr = ` (${Math.abs(diffRound)}w ahead of median)`;
        } else if (diffRound > 0) {
          diffStr = ` (${diffRound}w behind median)`;
        } else {
          diffStr = ' (on median)';
        }
      }
      return (
        <div className="text-[11px] font-medium text-ink-3">
          <span>{t('status.achieved', 'Achieved')}{dateStr ? ` on ${dateStr}` : ''}{diffStr}</span>
        </div>
      );
    }

    if (m.status === 'now') {
      const dateStr = m.startedAt ? formatDate(m.startedAt) : '';
      return (
        <div className="text-[11px] font-medium text-ink-3">
          <span>{t('status.started', 'Started')}{dateStr ? ` on ${dateStr}` : ''}</span>
        </div>
      );
    }

    if (dobIso) {
      const startD = dateFromWeeks(m.startWeeks);
      const medianD = dateFromWeeks(m.medianWeeks);
      return (
        <div className="flex flex-col gap-0.5 text-[11px] font-medium text-ink-3">
          {/* Line 1: average start */}
          <span>{t('milestones.stats.avgStart', 'Avg start')}: <strong className="text-ink-2">wk {startW}</strong> ({startD})</span>
          {/* Line 2: median + standard deviation */}
          <span>
            {t('milestones.stats.median', 'Median')}: <strong className="text-ink-2">wk {medianW}</strong> ({medianD})
            <span className="mx-1.5 text-hairline">|</span>
            {t('milestones.stats.stdDev', 'SD')}: <strong className="text-ink-2">±{stdDevStr} w</strong>
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-0.5 text-[11px] font-medium text-ink-3">
        {/* Line 1: average start */}
        <span>{t('milestones.stats.avgStart', 'Avg start')}: <strong className="text-ink-2">wk {startW}</strong></span>
        {/* Line 2: median + standard deviation */}
        <span>
          {t('milestones.stats.median', 'Median')}: <strong className="text-ink-2">wk {medianW}</strong>
          <span className="mx-1.5 text-hairline">|</span>
          {t('milestones.stats.stdDev', 'SD')}: <strong className="text-ink-2">±{stdDevStr} w</strong>
        </span>
      </div>
    );
  };

  return (
    <article
      className="relative overflow-hidden rounded-xl border border-line bg-surface transition-all"
      // Skip layout/paint for off-screen cards in the 130+ list; the reserved
      // size keeps the scrollbar honest until a card scrolls into view.
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 96px' }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggle?.(false)}
        className="flex items-stretch w-full cursor-pointer min-h-11"
      >
        {/* Left 1/3: Thumbnail or Category Icon */}
        <div
          className="w-1/3 relative shrink-0 overflow-hidden flex items-center justify-center"
          style={{ background: url ? 'var(--color-tint)' : `${meta.color}14` }}
        >
          {url ? (
            <img
              src={url}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 size-full object-cover object-center"
            />
          ) : (
            <Icon name={meta.icon} size={32} color={meta.color} />
          )}

          {/* Group key overlay */}
          <div 
            className="absolute top-2 left-2 z-10 rounded-md bg-surface/85 backdrop-blur-[2px] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] shadow-xs max-w-[calc(100%-16px)] truncate" 
            style={{ color: meta.color }}
            title={t('milestones.group.' + m.group, m.group)}
          >
            {t('milestones.group.' + m.group, m.group)}
          </div>
        </div>

        {/* Right 2/3: Texts and Status */}
        <div className="w-2/3 flex flex-col justify-between p-3.5 pr-4 pl-3.5 min-w-0">
          <div className="flex flex-col gap-1.5">
            {/* Top row: warning badge on the left, plus StatusPill on the right */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                {isWarning && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle?.(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-1.5 text-[11.5px] font-semibold tracking-wide bg-amber-soft text-amber-ink hover:bg-amber/20 transition-colors"
                    title="Baby is past the early flag week. Tap for typical timing."
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0"
                    >
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Warning
                  </button>
                )}
                
                {isInfo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle?.(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-1.5 text-[11.5px] font-semibold tracking-wide transition-colors"
                    style={{
                      background: 'rgba(59, 130, 246, 0.12)',
                      color: '#2563eb',
                    }}
                    title="Baby is behind the median age for this milestone. Tap for typical timing."
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    Late
                  </button>
                )}
              </div>

              {onCycle ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCycle();
                  }}
                  className="shrink-0 cursor-pointer"
                >
                  <StatusPill status={m.status} />
                </div>
              ) : (
                <div className="shrink-0">
                  <StatusPill status={m.status} />
                </div>
              )}
            </div>

            {/* Name */}
            <h3 className="font-serif text-[17px] leading-snug tracking-tight text-ink font-semibold mt-1">
              {m.name}
            </h3>

            {/* Description */}
            <p className="text-[13px] leading-snug text-ink-2 text-pretty line-clamp-2">{m.desc}</p>
          </div>

          <div className="mt-2.5 flex flex-col gap-2.5">
            {/* Dates / Stats */}
            {renderTimingStats()}
          </div>
        </div>
      </div>
    </article>
  );
}
